import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Users } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { LANGUAGES, POST_CHAR_LIMIT } from '@olimhub/shared'
import type { Language, OfferType, OfferPost } from '@olimhub/shared'
import { postsApi } from '@/api'
import { useToast } from '@/components/common/Toast'
import { ImageUpload } from '@/components/post/ImageUpload'
import { useIsDesktop, useCategoryOptions } from '@/hooks'
import type { PostFormGroupContext } from '@/context/PostFormContext'

const UI_LANGUAGES = LANGUAGES.filter((l) => l !== 'he')

function resolveUiLang(lang: string): Language {
  const code = lang.slice(0, 2)
  return (UI_LANGUAGES as readonly string[]).includes(code) ? (code as Language) : 'en'
}

interface LocationState {
  content: string
  language: Language
  imageUrl?: string | null
  groupContext?: PostFormGroupContext
  crossPost?: boolean
  postAsGroup?: boolean
  initialPost?: OfferPost
}

const INPUT_STYLE: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--border)',
  background: 'var(--bg-sunk)',
  color: 'var(--text-strong)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body)',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
}

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: 6,
}

const CARD_STYLE: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
  padding: 16,
  marginBottom: 12,
}

export function BazaarForm() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t, i18n } = useTranslation()
  const isDesktop = useIsDesktop()

  const state = (location.state ?? {}) as Partial<LocationState>
  const editingPost = state.initialPost

  const [content, setContent] = useState(editingPost?.content ?? state.content ?? '')
  const [language, setLanguage] = useState<Language>(
    editingPost ? resolveUiLang(editingPost.language) : (state.language ?? resolveUiLang(i18n.language)),
  )
  const [imageUrl, setImageUrl] = useState<string | null>(editingPost?.imageUrl ?? state.imageUrl ?? null)
  const [offerType, setOfferType] = useState<OfferType>(editingPost?.offer.offerType ?? 'good')
  const [categoryId, setCategoryId] = useState<string | null>(editingPost?.category?.id ?? null)
  const { options: categoryOptions, language: categoryLang } = useCategoryOptions('offer')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [crossPost, setCrossPost] = useState(editingPost?.showInLocalFeed ?? state.crossPost ?? false)
  // See PostForm.tsx — same unified-entry identity picker pattern. Identity
  // can't change on edit (no picker shown then — see showCrossPostOnEdit).
  const [postAsGroup, setPostAsGroup] = useState(state.postAsGroup ?? false)
  const groupContext = state.groupContext
  const isPostingAsGroup = !!groupContext?.canPostAsGroup && postAsGroup
  const showCrossPostOnEdit = !!editingPost && (editingPost.account.accountType === 'group' || !!editingPost.groupTag)

  const remaining = POST_CHAR_LIMIT - content.length
  const isOverLimit = remaining < 0
  const isEmpty = content.trim().length === 0
  const hasBody = !isEmpty || !!imageUrl
  const isFormValid = hasBody && !isOverLimit

  const handleSubmit = async () => {
    if (isSubmitting) return
    if (!isFormValid) {
      setSubmitAttempted(true)
      return
    }
    setIsSubmitting(true)
    try {
      if (editingPost) {
        await postsApi.update(editingPost.id, {
          content,
          language,
          imageUrl: imageUrl ?? undefined,
          offerType,
          categoryId,
          showInLocalFeed: showCrossPostOnEdit ? crossPost : undefined,
        })
        await queryClient.invalidateQueries({ queryKey: ['post', editingPost.id] })
        await queryClient.invalidateQueries({ queryKey: ['feed'] })
        await queryClient.invalidateQueries({ queryKey: ['group-feed'] })
        toast.success(t('post.save_changes'))
        navigate(`/posts/${editingPost.id}`)
        return
      }
      await postsApi.create({
        type: 'offer',
        content,
        language,
        imageUrl: imageUrl ?? undefined,
        offerType,
        categoryId,
        postAsGroupSlug: isPostingAsGroup ? groupContext!.slug : undefined,
        groupTagSlug: groupContext && !isPostingAsGroup ? groupContext.slug : undefined,
        showInLocalFeed: groupContext ? crossPost : undefined,
      })
      if (groupContext) await queryClient.invalidateQueries({ queryKey: ['group-feed', groupContext.slug] })
      if (!groupContext || crossPost) await queryClient.invalidateQueries({ queryKey: ['feed'] })
      toast.success('Bazaar post created!')
      navigate(groupContext ? `/groups/${groupContext.slug}` : '/')
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const offerTypes: Array<{ value: OfferType; label: string }> = [
    { value: 'good', label: t('post.offer_good') },
    { value: 'service', label: t('post.offer_service') },
  ]

  // Desktop: shares the language-selector row, pushed to the far end.
  // Mobile: stays in its original spot further down the form.
  const crossPostSwitch = (!!groupContext || showCrossPostOnEdit) ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
        {t('groups.cross_post_toggle')}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={crossPost}
        onClick={() => setCrossPost((v) => !v)}
        style={{
          width: 44, height: 26,
          borderRadius: 'var(--radius-pill)',
          border: 'none',
          background: crossPost ? 'var(--brand)' : 'var(--border-strong)',
          cursor: 'pointer',
          position: 'relative',
          flexShrink: 0,
          transition: 'background var(--dur-base) var(--ease-out)',
        }}
      >
        <span style={{
          position: 'absolute',
          top: 3,
          insetInlineStart: crossPost ? 'calc(100% - 23px)' : 3,
          width: 20, height: 20,
          borderRadius: '50%',
          background: '#fff',
          transition: 'inset-inline-start var(--dur-base) var(--ease-out)',
        }} />
      </button>
    </div>
  ) : null

  return (
    <div style={{ maxWidth: 'var(--feed-max)', margin: '0 auto', paddingBottom: 80 }}>
      {/* Sticky header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            padding: 4,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 'var(--fs-body)',
            color: 'var(--text-strong)',
          }}
        >
          {editingPost ? t('post.edit_bazaar') : t('post.new_bazaar')}
        </span>
      </div>

      {groupContext && (
        groupContext.canPostAsGroup ? (
          <div style={{ margin: '10px 16px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)' }}>
              {t('groups.post_as_label')}
            </span>
            {([
              { value: false, label: t('groups.post_as_you') },
              { value: true, label: groupContext.name },
            ] as const).map(({ value, label }) => (
              <button
                key={String(value)}
                type="button"
                onClick={() => setPostAsGroup(value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-pill)',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 700,
                  background: postAsGroup === value ? 'var(--brand)' : 'var(--surface-2)',
                  color: postAsGroup === value ? 'var(--on-brand)' : 'var(--text-muted)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        ) : (
          <div
            style={{
              margin: '10px 16px 0',
              padding: '6px 10px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--brand-soft)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 600,
              color: 'var(--brand)',
            }}
          >
            <Users size={14} />
            {t('groups.posting_in', { name: groupContext.name })}
          </div>
        )
      )}

      <div style={{ padding: '12px 12px 0' }}>
        {/* Content */}
        <div style={CARD_STYLE}>
          <label style={LABEL_STYLE}>{t('post.content_label')}</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            style={{ ...INPUT_STYLE, minHeight: 80, resize: 'vertical' }}
          />
          {submitAttempted && !hasBody && (
            <span style={{ display: 'block', marginTop: 4, fontSize: 'var(--fs-xs)', color: 'var(--danger)', fontFamily: 'var(--font-sans)' }}>
              {t('post.text_or_image_required')}
            </span>
          )}
          <div
            style={{
              textAlign: 'end',
              fontSize: 'var(--fs-xs)',
              fontFamily: 'var(--font-sans)',
              color: remaining < 20 ? 'var(--danger)' : 'var(--text-faint)',
              marginTop: 4,
            }}
          >
            {content.length}/{POST_CHAR_LIMIT}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 12,
              justifyContent: isDesktop ? 'space-between' : 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', gap: 6 }}>
              {UI_LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setLanguage(lang)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-pill)',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 600,
                    background: language === lang ? 'var(--brand)' : 'var(--surface-2)',
                    color: language === lang ? 'var(--on-brand)' : 'var(--text-muted)',
                    textTransform: 'uppercase',
                  }}
                >
                  {lang}
                </button>
              ))}
            </div>
            {isDesktop && crossPostSwitch}
          </div>

          <ImageUpload
            onUploaded={(url) => setImageUrl(url)}
            onRemoved={() => setImageUrl(null)}
            currentUrl={imageUrl}
          />
        </div>

        {/* Offer details */}
        <div style={CARD_STYLE}>
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE}>{t('post.offer_type')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {offerTypes.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setOfferType(value)}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 'var(--radius-pill)',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-sm)',
                    fontWeight: 600,
                    background: offerType === value ? 'var(--brand)' : 'var(--surface-2)',
                    color: offerType === value ? 'var(--on-brand)' : 'var(--text-muted)',
                    transition: 'background var(--dur-fast), color var(--dur-fast)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={LABEL_STYLE}>{t('post.category')} ({t('events.optional')})</label>
            <select
              value={categoryId ?? ''}
              onChange={(e) => setCategoryId(e.target.value || null)}
              style={INPUT_STYLE}
            >
              <option value="">{t('post.category_none')}</option>
              {categoryOptions.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.translations[categoryLang] ?? cat.translations.en}
                </option>
              ))}
            </select>
          </div>

          {!isDesktop && crossPostSwitch && <div style={{ marginTop: 14 }}>{crossPostSwitch}</div>}
        </div>

        {/* Submit */}
        <button
          type="button"
          onClick={() => { void handleSubmit() }}
          style={{
            width: '100%',
            padding: '13px 0',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'var(--brand)',
            color: 'var(--on-brand)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 'var(--fs-body)',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isFormValid ? 1 : 0.55,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            transition: 'opacity var(--dur-fast)',
          }}
        >
          {isSubmitting && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
          {editingPost ? t('post.save_changes') : t('post.post_to_bazaar')}
        </button>
      </div>
    </div>
  )
}
